import type { EnvironmentPullRequestRow } from "@afar-research/client-runtime/state/pull-requests";
import type {
  PullRequestActor,
  PullRequestLabel,
  PullRequestListState,
} from "@afar-research/contracts";

export interface PullRequestAuthorFacet {
  readonly actor: PullRequestActor;
  readonly count: number;
  readonly mergedCount: number;
}

export interface PullRequestLabelFacet extends PullRequestLabel {
  readonly count: number;
}

function normalize(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * The authors and labels the loaded rows actually carry, so the filter menu offers what this
 * list can be narrowed to rather than a free-text box that narrows to nothing when misspelled.
 * Following T3Code: authors are ranked by merged work first, labels by how many rows wear them.
 *
 * An author is counted even where the row is out of the chosen state, so switching to "Merged"
 * does not empty the author menu the reader is standing in; its label counts follow the state.
 */
export function collectPullRequestListFacets(
  rows: ReadonlyArray<EnvironmentPullRequestRow>,
  state: PullRequestListState,
): {
  readonly authors: ReadonlyArray<PullRequestAuthorFacet>;
  readonly labels: ReadonlyArray<PullRequestLabelFacet>;
} {
  const authors = new Map<string, PullRequestAuthorFacet>();
  const labels = new Map<string, PullRequestLabelFacet>();
  for (const row of rows) {
    const inState = state === "all" || row.state === state;
    if (row.author !== null) {
      const key = normalize(row.author.login);
      if (key !== null) {
        const held = authors.get(key);
        authors.set(key, {
          actor: held?.actor ?? row.author,
          count: (held?.count ?? 0) + Number(inState),
          mergedCount: (held?.mergedCount ?? 0) + Number(row.state === "merged"),
        });
      }
    }
    if (!inState) continue;
    for (const label of row.labels) {
      const key = normalize(label.name);
      if (key === null) continue;
      const held = labels.get(key);
      labels.set(key, {
        name: held?.name ?? label.name,
        color: held?.color ?? label.color,
        count: (held?.count ?? 0) + 1,
      });
    }
  }
  return {
    authors: [...authors.values()]
      .filter((author) => author.count > 0)
      .toSorted(
        (left, right) =>
          right.mergedCount - left.mergedCount ||
          right.count - left.count ||
          left.actor.login.localeCompare(right.actor.login),
      ),
    labels: [...labels.values()].toSorted(
      (left, right) => right.count - left.count || left.name.localeCompare(right.name),
    ),
  };
}
